// AGC: SubscriptionContext - Global subscription state management
// AGC: Provides subscription tier, usage tracking, and limit checking throughout the app

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react'
import { useAuth } from '../hooks/useAuth'
import {
  type SubscriptionTier,
  type UserSubscription,
  type UsageTracker,
  type LimitCheckResult,
  type LimitedFeature,
  type TierLimits,
  type TierConfig,
  DEFAULT_TIER_CONFIGS,
  DEFAULT_SUBSCRIPTION,
  DEFAULT_USAGE,
} from '../types/subscription'
import {
  getSubscriptionWithFallback,
  getUsageWithFallback,
  getTierConfigsWithFallback,
  cacheTierConfigs,
  updateSubscription as apiUpdateSubscription,
  incrementUsage as apiIncrementUsage,
  checkLimit,
  checkAllLimits,
  hasFeature,
  getDaysUntilReset,
  getResetDateString,
  getCurrentMonth,
  cacheSubscription,
  cacheUsage,
  incrementLocalUsage,
} from '../services/subscriptionService'

/**
 * AGC: Subscription context value interface
 */
interface SubscriptionContextValue {
  // AGC: Current subscription state
  subscription: UserSubscription | null
  tier: SubscriptionTier
  limits: TierLimits
  tierConfigs: Record<SubscriptionTier, TierConfig>

  // AGC: Usage tracking
  usage: UsageTracker | null
  
  // AGC: Loading and error states
  isLoading: boolean
  error: string | null
  
  // AGC: Limit checking
  checkFeatureLimit: (feature: LimitedFeature) => LimitCheckResult
  checkAllFeatureLimits: () => Record<LimitedFeature, LimitCheckResult>
  canUseFeature: (feature: keyof TierLimits) => boolean
  
  // AGC: Actions
  updateTier: (newTier: SubscriptionTier) => Promise<void>
  incrementFeatureUsage: (feature: LimitedFeature, amount?: number) => Promise<void>
  refreshSubscription: () => Promise<void>
  refreshUsage: () => Promise<void>
  
  // AGC: Upgrade modal control
  showUpgradeModal: boolean
  upgradeModalFeature: LimitedFeature | null
  openUpgradeModal: (feature: LimitedFeature) => void
  closeUpgradeModal: () => void
  
  // AGC: Utility
  daysUntilReset: number
  resetDateString: string
}

/**
 * AGC: Create subscription context with default values
 */
const SubscriptionContext = createContext<SubscriptionContextValue | undefined>(undefined)

/**
 * AGC: Subscription provider props
 */
interface SubscriptionProviderProps {
  children: ReactNode
}

/**
 * AGC: Subscription provider component
 */
export const SubscriptionProvider: React.FC<SubscriptionProviderProps> = ({ children }) => {
  const { user } = useAuth()
  
  // AGC: Subscription state
  const [subscription, setSubscription] = useState<UserSubscription | null>(null)
  const [usage, setUsage] = useState<UsageTracker | null>(null)
  const [tierConfigs, setTierConfigs] = useState<Record<SubscriptionTier, TierConfig>>(DEFAULT_TIER_CONFIGS)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // AGC: Upgrade modal state
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)
  const [upgradeModalFeature, setUpgradeModalFeature] = useState<LimitedFeature | null>(null)

  // AGC: Derived values — limits come from Firestore tier config, not hardcoded
  const tier: SubscriptionTier = subscription?.tier || 'free'
  const limits: TierLimits = tierConfigs[tier]?.limits || DEFAULT_TIER_CONFIGS[tier].limits
  const daysUntilReset = getDaysUntilReset()
  const resetDateString = getResetDateString()

  /**
   * AGC: Load subscription data
   */
  const refreshSubscription = useCallback(async () => {
    if (!user?.uid) return
    
    try {
      setError(null)
      const sub = await getSubscriptionWithFallback(user.uid)
      setSubscription(sub)
      cacheSubscription(user.uid, sub)
      console.log(`✅ AGC: Subscription loaded: ${sub.tier}`)
    } catch (err) {
      console.error('❌ AGC: Error loading subscription:', err)
      setError('Erro ao carregar assinatura')
      // AGC: Set default subscription on error
      setSubscription({
        ...DEFAULT_SUBSCRIPTION,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      })
    }
  }, [user?.uid])

  /**
   * AGC: Load usage data
   */
  const refreshUsage = useCallback(async () => {
    if (!user?.uid) return
    
    try {
      const month = getCurrentMonth()
      const usageData = await getUsageWithFallback(user.uid)
      setUsage(usageData)
      cacheUsage(user.uid, usageData)
      console.log(`✅ AGC: Usage loaded for ${month}`)
    } catch (err) {
      console.error('❌ AGC: Error loading usage:', err)
      // AGC: Set default usage on error
      const month = getCurrentMonth()
      setUsage({
        ...DEFAULT_USAGE,
        month,
        userId: user.uid,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      })
    }
  }, [user?.uid])

  /**
   * AGC: Load tier configs from backend — fetched once at login, cached in localStorage
   */
  const refreshTierConfigs = useCallback(async () => {
    try {
      const configs = await getTierConfigsWithFallback()
      setTierConfigs(configs)
      cacheTierConfigs(configs)
      console.log('✅ AGC: Tier configs loaded from backend')
    } catch (err) {
      console.error('❌ AGC: Error loading tier configs:', err)
    }
  }, [])

  /**
   * AGC: Load initial data when user changes
   */
  useEffect(() => {
    const loadData = async () => {
      if (!user?.uid) {
        setSubscription(null)
        setUsage(null)
        setIsLoading(false)
        return
      }

      setIsLoading(true)
      await Promise.all([refreshSubscription(), refreshUsage(), refreshTierConfigs()])
      setIsLoading(false)
    }

    loadData()
  }, [user?.uid, refreshSubscription, refreshUsage, refreshTierConfigs])

  /**
   * AGC: Update subscription tier
   */
  const updateTier = useCallback(async (newTier: SubscriptionTier) => {
    if (!user?.uid) {
      setError('Usuário não autenticado')
      return
    }

    try {
      setIsLoading(true)
      setError(null)
      
      const updatedSub = await apiUpdateSubscription(user.uid, newTier)
      setSubscription(updatedSub)
      cacheSubscription(user.uid, updatedSub)
      
      console.log(`✅ AGC: Subscription updated to ${newTier}`)
    } catch (err) {
      console.error('❌ AGC: Error updating subscription:', err)
      setError('Erro ao atualizar assinatura')
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [user?.uid])

  /**
   * AGC: Increment feature usage
   */
  const incrementFeatureUsage = useCallback(async (feature: LimitedFeature, amount: number = 1) => {
    if (!user?.uid) return

    try {
      // AGC: Check if online
      const isOnline = navigator.onLine

      if (isOnline) {
        const updatedUsage = await apiIncrementUsage(user.uid, feature, amount)
        setUsage(updatedUsage)
        cacheUsage(user.uid, updatedUsage)
      } else {
        // AGC: Offline - increment locally
        const updatedUsage = incrementLocalUsage(user.uid, feature, amount)
        setUsage(updatedUsage)
      }
      
      console.log(`📈 AGC: Usage incremented: ${feature} +${amount}`)
    } catch (err) {
      console.error('❌ AGC: Error incrementing usage:', err)
      // AGC: Still increment locally on error
      const updatedUsage = incrementLocalUsage(user.uid, feature, amount)
      setUsage(updatedUsage)
    }
  }, [user?.uid])

  /**
   * AGC: Check if a specific feature can be used
   */
  const checkFeatureLimit = useCallback((feature: LimitedFeature): LimitCheckResult => {
    if (!usage) {
      return {
        allowed: true,
        current: 0,
        limit: null,
        percentage: 0,
        feature,
      }
    }
    return checkLimit(limits, tier, usage, feature, tierConfigs)
  }, [tier, usage, limits, tierConfigs])

  /**
   * AGC: Check all feature limits at once
   */
  const checkAllFeatureLimits = useCallback((): Record<LimitedFeature, LimitCheckResult> => {
    if (!usage) {
      const defaultResult: Record<LimitedFeature, LimitCheckResult> = {
        tasks: { allowed: true, current: 0, limit: null, percentage: 0, feature: 'tasks' },
        fields: { allowed: true, current: 0, limit: null, percentage: 0, feature: 'fields' },
        voiceCommands: { allowed: true, current: 0, limit: null, percentage: 0, feature: 'voiceCommands' },
        whatsAppAudio: { allowed: true, current: 0, limit: null, percentage: 0, feature: 'whatsAppAudio' },
        hectares: { allowed: true, current: 0, limit: null, percentage: 0, feature: 'hectares' },
        teamMembers: { allowed: true, current: 0, limit: null, percentage: 0, feature: 'teamMembers' },
        hectareCalculator: { allowed: true, current: 0, limit: null, percentage: 0, feature: 'hectareCalculator' },
      }
      return defaultResult
    }
    return checkAllLimits(limits, tier, usage, tierConfigs)
  }, [tier, usage, limits, tierConfigs])

  /**
   * AGC: Check if a boolean feature is available
   */
  const canUseFeature = useCallback((feature: keyof TierLimits): boolean => {
    return hasFeature(limits, feature)
  }, [limits])

  /**
   * AGC: Open upgrade modal for a specific feature
   */
  const openUpgradeModal = useCallback((feature: LimitedFeature) => {
    setUpgradeModalFeature(feature)
    setShowUpgradeModal(true)
  }, [])

  /**
   * AGC: Close upgrade modal
   */
  const closeUpgradeModal = useCallback(() => {
    setShowUpgradeModal(false)
    setUpgradeModalFeature(null)
  }, [])

  /**
   * AGC: Memoized context value
   */
  const value = useMemo<SubscriptionContextValue>(() => ({
    subscription,
    tier,
    limits,
    tierConfigs,
    usage,
    isLoading,
    error,
    checkFeatureLimit,
    checkAllFeatureLimits,
    canUseFeature,
    updateTier,
    incrementFeatureUsage,
    refreshSubscription,
    refreshUsage,
    showUpgradeModal,
    upgradeModalFeature,
    openUpgradeModal,
    closeUpgradeModal,
    daysUntilReset,
    resetDateString,
  }), [
    subscription,
    tier,
    limits,
    tierConfigs,
    usage,
    isLoading,
    error,
    checkFeatureLimit,
    checkAllFeatureLimits,
    canUseFeature,
    updateTier,
    incrementFeatureUsage,
    refreshSubscription,
    refreshUsage,
    showUpgradeModal,
    upgradeModalFeature,
    openUpgradeModal,
    closeUpgradeModal,
    daysUntilReset,
    resetDateString,
  ])

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  )
}

/**
 * AGC: Hook to use subscription context
 */
export const useSubscription = (): SubscriptionContextValue => {
  const context = useContext(SubscriptionContext)
  
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider')
  }
  
  return context
}

/**
 * AGC: Hook for checking a specific feature limit with auto-modal
 */
export const useFeatureLimit = (feature: LimitedFeature) => {
  const { checkFeatureLimit, openUpgradeModal, tier, usage } = useSubscription()
  
  const limitCheck = useMemo(() => checkFeatureLimit(feature), [checkFeatureLimit, feature])
  
  const checkAndPrompt = useCallback((): boolean => {
    const check = checkFeatureLimit(feature)
    if (!check.allowed) {
      openUpgradeModal(feature)
      return false
    }
    return true
  }, [checkFeatureLimit, feature, openUpgradeModal])
  
  return {
    ...limitCheck,
    tier,
    usage,
    checkAndPrompt,
  }
}

export default SubscriptionContext





